import {AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, Output, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {OrganisationsFilterDto} from './organisations-filter.dto';

const EMPTY_FILTER = {
    name: '',
    code: '',
    address: '',
    city: '',
    postcode: '',
    showDeactivated: false,
    showDeleted: false,
};

/**
 * UNIVAPLAT-3691: overlay filter for the organisations list, modelled on the contracts filter.
 * The "contains" match is the backend's job; this component only collects values. Hiding the
 * show-deactivated / deleted checkbox (`canSeeInactive`) is a convenience, the API enforces.
 */
@Component({
    selector: 'cdss-organisations-filter',
    imports: [ReactiveFormsModule],
    standalone: true,
    templateUrl: './organisations-filter.component.html',
    styleUrl: './organisations-filter.component.scss'
})
export class OrganisationsFilterComponent implements AfterViewInit {

    form!: FormGroup;

    /** True for a general admin: shows the "Show deactivated / deleted organisations" checkbox. */
    @Input() canSeeInactive = false;

    @Output() apply = new EventEmitter<boolean>();
    @Output() cancel = new EventEmitter<{}>();
    @Output() filterChange = new EventEmitter<OrganisationsFilterDto>();

    private host = inject<ElementRef<HTMLElement>>(ElementRef);

    @ViewChild('firstField') firstField?: ElementRef<HTMLInputElement>;

    _filter!: OrganisationsFilterDto;

    get filter(): OrganisationsFilterDto {
        return this._filter;
    }

    @Input()
    set filter(value: OrganisationsFilterDto) {
        this._filter = value;
        this.initForm();
    }

    constructor(private fb: FormBuilder) {
        this.form = this.fb.group({...EMPTY_FILTER});
    }

    ngAfterViewInit(): void {
        // Keyboard users: move focus into the overlay when it opens.
        this.firstField?.nativeElement.focus();
    }

    initForm(): void {
        if (!this.filter) return;
        this.form.reset({
            name: this.filter.name || '',
            code: this.filter.code || '',
            address: this.filter.address || '',
            city: this.filter.city || '',
            postcode: this.filter.postcode || '',
            showDeactivated: this.filter.showDeactivated || false,
            showDeleted: this.filter.showDeleted || false,
        });
    }

    reset(): void {
        this.form.reset({...EMPTY_FILTER});
        this.returnValue();
        this.apply.emit();
    }

    applyFilter(): void {
        this.returnValue();
        this.apply.emit();
    }

    returnValue(): void {
        const value = this.form.value;
        this.filter.name = (value.name || '').trim();
        this.filter.code = (value.code || '').trim();
        this.filter.address = (value.address || '').trim();
        this.filter.city = (value.city || '').trim();
        this.filter.postcode = (value.postcode || '').trim();
        // The single checkbox drives both flags; an organisation admin never sees it, so never send them.
        const showInactive = this.canSeeInactive && !!value.showDeactivated;
        this.filter.showDeactivated = showInactive;
        this.filter.showDeleted = showInactive;
        this.filterChange.emit(this.filter);
    }

    /** Keeps Tab / Shift+Tab inside the overlay (aria-modal): wraps at the first and last control. */
    trapFocus(event: KeyboardEvent): void {
        const focusable = Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ));
        if (focusable.length === 0) {
            return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && (active === first || !this.host.nativeElement.contains(active))) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && (active === last || !this.host.nativeElement.contains(active))) {
            event.preventDefault();
            first.focus();
        }
    }

    close(): void {
        this.cancel.emit({});
    }
}
